import React from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Button, Grid, Paper, Chip, Stack, Divider, Container,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import GavelIcon from '@mui/icons-material/Gavel';
import PaymentIcon from '@mui/icons-material/Payment';
import UndoIcon from '@mui/icons-material/Undo';
import services from '../data/services';

// ── Per-tier TOS summary ─────────────────────────────────────────────────────

const tosByTier: Record<string, {
  payment: string[];
  refund: string[];
}> = {
  Custom: {
    payment: [
      'Pricing quoted individually — reach out to discuss before commissioning.',
      'Payments via PayPal.',
      'Payment terms (upfront or split) agreed per project.',
      'Payment plans available on request.',
    ],
    refund: [
      'Refund terms agreed on a per-project basis before work begins.',
      'Once work has started, partial refunds may apply depending on progress.',
      'Please confirm you are happy with the quoted scope before paying.',
    ],
  },
  'Low Poly': {
    payment: [
      'Starts at $200. Full payment required upfront before work begins.',
      'Payments via PayPal.',
      'Payment plans available on request.',
    ],
    refund: [
      'No refunds on low poly commissions.',
      'Please ensure you are happy with the terms before paying.',
    ],
  },
  'High Poly': {
    payment: [
      '50% upfront payment charged at the start.',
      'Remaining 50% charged before model handover.',
      'Payments via PayPal.',
    ],
    refund: [
      'After modelling starts, full refunds are not provided.',
      'If you cancel: I keep the 50% deposit and retain all rights to the model.',
      'If I cancel: I refund the deposit and dispose of all WIPs.',
      'No refund requests accepted after model handover.',
    ],
  },
};

const sharedTerms = [
  'All rights reserved by panko. The artist holds the copyright of the commissioned model.',
  'The artist may promote and post the work as portfolio.',
  'You may use the model for personal use, streaming, social media, and social games.',
  'Commercial use or reselling requires additional fees (at least 200% of original price) and proper credit.',
  'NDA available for debuts or confidential work — additional 10% of total price.',
];

// ── Component ────────────────────────────────────────────────────────────────

function BulletList({ items, color }: { items: string[]; color: string }) {
  return (
    <Stack spacing={1.2}>
      {items.map((item, i) => (
        <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
          <Box sx={{ mt: '7px', width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>{item}</Typography>
        </Stack>
      ))}
    </Stack>
  );
}

export default function CommissionPage() {
  const { id, tierIndex } = useParams<{ id: string; tierIndex: string }>();
  const service = services.find((s) => s.id === id);
  const tier = service?.pricingTiers[Number(tierIndex)];

  if (!service || !tier) {
    return (
      <Container sx={{ py: 16, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ mb: 3 }}>Commission info not found</Typography>
        <Button component={RouterLink} to="/#services" startIcon={<ArrowBackIcon />} variant="outlined">
          Back to Services
        </Button>
      </Container>
    );
  }

  const { color } = service;
  const tos = tosByTier[tier.name];

  return (
    <Box sx={{ background: '#0d0a12', minHeight: '100vh', pt: 10 }}>
      {/* Hero */}
      <Box sx={{
        py: { xs: 8, md: 10 },
        px: { xs: 3, md: 8 },
        background: `radial-gradient(ellipse 70% 50% at 60% 0%, ${color}18 0%, transparent 70%), #0d0a12`,
        borderBottom: '1px solid rgba(255,110,180,0.1)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundImage: `linear-gradient(${color}06 1px, transparent 1px), linear-gradient(90deg, ${color}06 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
          maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black, transparent)',
        }} />
        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Button
            component={RouterLink}
            to={`/services/${service.id}`}
            startIcon={<ArrowBackIcon />}
            sx={{ color: 'text.secondary', mb: 4, '&:hover': { color: 'primary.main' } }}
          >
            {service.title}
          </Button>

          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: '2.5rem', filter: `drop-shadow(0 0 16px ${color}88)` }}>
              {service.emoji}
            </Typography>
            <Chip
              label={tier.name}
              sx={{
                background: `${color}22`, border: `1px solid ${color}55`,
                color, fontWeight: 700, fontSize: '0.8rem',
              }}
            />
            {tier.highlighted && (
              <Chip label="Most Popular" size="small" sx={{
                background: `linear-gradient(135deg, ${color}, ${color}cc)`,
                color: '#fff', fontWeight: 700,
              }} />
            )}
          </Stack>

          <Typography variant="h1" sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' }, lineHeight: 1.1, mb: 1 }}>
            {service.title} — {tier.name}
          </Typography>
          <Typography variant="h4" sx={{ color, fontWeight: 700, mb: 2, fontSize: '1.8rem' }}>
            {tier.price}
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: 5, maxWidth: 560, lineHeight: 1.8 }}>
            {tier.description}
          </Typography>

          {service.vgenUrl && (
            <Button
              variant="contained"
              size="large"
              href={service.vgenUrl}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNewIcon />}
              sx={{
                background: `linear-gradient(135deg, ${color}, ${color}cc)`,
                px: 5, py: 1.5, fontSize: '1rem',
                '&:hover': { boxShadow: `0 0 30px ${color}55` },
              }}
            >
              Commission on VGen
            </Button>
          )}
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={6}>
          {/* Left column */}
          <Grid size={{ xs: 12, md: 8 }}>

            {/* What's included */}
            <Box sx={{ mb: 8 }}>
              <Typography variant="overline" sx={{ color, letterSpacing: 4 }}>DELIVERABLES</Typography>
              <Typography variant="h3" sx={{ fontSize: { xs: '1.8rem', md: '2.2rem' }, mt: 1, mb: 4 }}>
                What's Included
              </Typography>
              <Stack spacing={1.5}>
                {tier.features.map((f) => (
                  <Stack key={f} direction="row" spacing={1.5} alignItems="flex-start">
                    <CheckCircleOutlineIcon sx={{ color, fontSize: 18, mt: 0.15, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>{f}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>

            {/* Commission add-ons (at time of order) */}
            {tier.addons && tier.addons.length > 0 && (
              <Box sx={{ mb: 8 }}>
                <Typography variant="overline" sx={{ color, letterSpacing: 4 }}>ADD-ONS</Typography>
                <Typography variant="h3" sx={{ fontSize: { xs: '1.8rem', md: '2.2rem' }, mt: 1, mb: 1 }}>
                  Commission Extras
                </Typography>
                <Typography sx={{ color: 'text.secondary', mb: 3, fontSize: '0.92rem' }}>
                  These can be added to your commission at the time of ordering.
                </Typography>
                <Stack spacing={1.5}>
                  {tier.addons.map((addon) => (
                    <Stack key={addon} direction="row" spacing={1.5} alignItems="flex-start">
                      <AddCircleOutlineIcon sx={{ color, fontSize: 18, mt: 0.15, flexShrink: 0 }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>{addon}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Post-delivery upgrades */}
            <Box sx={{ mb: 8 }}>
              <Typography variant="overline" sx={{ color, letterSpacing: 4 }}>UPGRADES</Typography>
              <Typography variant="h3" sx={{ fontSize: { xs: '1.8rem', md: '2.2rem' }, mt: 1, mb: 1 }}>
                After-Delivery Services
              </Typography>
              <Typography sx={{ color: 'text.secondary', mb: 4, fontSize: '0.92rem' }}>
                Once your model is delivered, you can commission upgrades and expansions as standalone services — no need to start from scratch.
              </Typography>
              <Grid container spacing={2}>
                {[
                  { label: 'New Outfit', desc: 'Additional outfit designed and rigged for your existing model, ready for hot-swapping in Warudo or VRChat.' },
                  { label: 'New Hairstyle', desc: 'Extra hairstyle with full physics rigging — great for matching different aesthetics or stream themes.' },
                  { label: 'Props & Accessories', desc: 'Standalone props, weapons, or accessories built to fit your model\'s scale and rig.' },
                  { label: 'Model Upgrade', desc: 'Upgrades to an existing model — topology improvements, re-texturing, expression additions, or platform support.' },
                ].map((item) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={item.label}>
                    <Paper sx={{
                      p: 3, height: '100%',
                      background: 'rgba(22,15,32,0.9)',
                      border: `1px solid ${color}18`,
                      transition: 'border-color 0.2s',
                      '&:hover': { borderColor: `${color}55` },
                    }}>
                      <Typography variant="body1" sx={{ fontWeight: 700, color, mb: 1 }}>{item.label}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>{item.desc}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Typography sx={{ color: 'text.secondary', mt: 3, fontSize: '0.85rem', fontStyle: 'italic' }}>
                Prices for upgrades are quoted individually depending on complexity. Reach out via VGen to discuss.
              </Typography>
            </Box>

            {/* How it works */}
            <Box sx={{ mb: 8 }}>
              <Typography variant="overline" sx={{ color, letterSpacing: 4 }}>PROCESS</Typography>
              <Typography variant="h3" sx={{ fontSize: { xs: '1.8rem', md: '2.2rem' }, mt: 1, mb: 4 }}>
                How It Works
              </Typography>
              <Stack spacing={2}>
                {service.process.map((step) => (
                  <Paper key={step.step} sx={{
                    p: 3,
                    background: 'rgba(22,15,32,0.9)',
                    border: `1px solid ${color}18`,
                    display: 'grid',
                    gridTemplateColumns: '48px 1fr',
                    gap: 2,
                    alignItems: 'start',
                  }}>
                    <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: `${color}66`, lineHeight: 1 }}>
                      {step.step}
                    </Typography>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>{step.title}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>{step.desc}</Typography>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            </Box>
          </Grid>

          {/* Right column — TOS */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ position: 'sticky', top: 100 }}>
              <Typography variant="overline" sx={{ color, letterSpacing: 4 }}>BEFORE YOU COMMISSION</Typography>
              <Typography variant="h4" sx={{ fontSize: '1.5rem', mt: 1, mb: 3 }}>
                Terms of Service
              </Typography>

              {/* Payment */}
              {tos && (
                <>
                  <Paper sx={{ p: 3, mb: 2, background: 'rgba(22,15,32,0.9)', border: `1px solid ${color}22` }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                      <PaymentIcon sx={{ color, fontSize: 18 }} />
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>Payment</Typography>
                    </Stack>
                    <BulletList items={tos.payment} color={color} />
                  </Paper>

                  <Paper sx={{ p: 3, mb: 2, background: 'rgba(22,15,32,0.9)', border: `1px solid ${color}22` }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                      <UndoIcon sx={{ color, fontSize: 18 }} />
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>Refunds</Typography>
                    </Stack>
                    <BulletList items={tos.refund} color={color} />
                  </Paper>
                </>
              )}

              <Paper sx={{ p: 3, mb: 3, background: 'rgba(22,15,32,0.9)', border: `1px solid ${color}22` }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <GavelIcon sx={{ color, fontSize: 18 }} />
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>General Terms</Typography>
                </Stack>
                <BulletList items={sharedTerms} color={color} />
              </Paper>

              <Button
                component={RouterLink}
                to="/tos"
                fullWidth
                variant="outlined"
                sx={{
                  borderColor: `${color}44`, color,
                  '&:hover': { borderColor: color, background: `${color}10` },
                  mb: 3,
                }}
              >
                Read Full TOS
              </Button>

              <Divider sx={{ borderColor: `${color}22`, mb: 3 }} />

              {service.vgenUrl && (
                <Button
                  fullWidth
                  variant="contained"
                  href={service.vgenUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<OpenInNewIcon />}
                  sx={{
                    background: `linear-gradient(135deg, ${color}, ${color}cc)`,
                    py: 1.5,
                    '&:hover': { boxShadow: `0 0 20px ${color}44` },
                  }}
                >
                  Commission on VGen
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
